import React, { useState, useEffect, useRef } from 'react';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import httpService from '../../services/httpService';
import { ApiEndpoints, Constants } from '../../constants/appConstants';
import { useExcelImportPopup } from '../Common/useExcelImportPopup';
import { useDataManagementFileUpload } from '../Common/useDataManagementFileUpload';
import { ImportTemplates } from '../../constants/appConstants';
import { createDialogOverlayStyles } from '../../utils/safeCssInjection';
import './StandardsComponent.scss';
import DataManagementForm from '../DataManagement/DataManagementForm';
import { dataManagementFormConfig, getFormValidationErrors, normalizeFieldValues } from '../DataManagement/dataManagementFormConfig';
import DataManagementLoader from '../DataManagement/DataManagementLoader';
import DataManagementDataTable, { DATA_MANAGEMENT_PAGE_SIZE } from '../DataManagement/DataManagementDataTable';
import { createDataManagementQuery, useDataManagementPagination } from '../DataManagement/useDataManagementPagination';

const renderStandardActions = (standard: any, onEdit: (standard: any) => void, onDelete: (standard: any) => void) => (
  <>
    <Button icon="pi pi-pencil" className="p-button-text" style={{ marginRight: 8 }} aria-label={`Edit ${standard.title}`} onClick={() => onEdit(standard)} />
    <Button icon="pi pi-trash" className="p-button-text p-button-danger" aria-label={`Delete ${standard.title}`} onClick={() => onDelete(standard)} />
  </>
);


interface StandardsComponentProps {
  providerTypes: { label: string; value: string }[];
}

const StandardsComponent: React.FC<StandardsComponentProps> = ({providerTypes}) => {
  // Fetch performance areas for dropdown
  const [performanceAreas, setPerformanceAreas] = useState<any[]>([]);
  const [lookupsLoading, setLookupsLoading] = useState(true);
  useEffect(() => {
    httpService.get(ApiEndpoints.GetAllPerformanceAreas)
      .then((response: any) => {
        let data = response.data;
        if (!Array.isArray(data)) data = [];
        setPerformanceAreas(data);
      })
      .catch(() => setPerformanceAreas([]))
      .finally(() => setLookupsLoading(false));
  }, []);
  React.useEffect(() => {
    const cleanup = createDialogOverlayStyles('standards-component-styles');
    return cleanup;
  }, []);

  const toast = useRef<Toast>(null);
  const { uploadFile } = useDataManagementFileUpload();
  const handleExcelUpload = async (file: File) => {
    setLoading(true);
    try {
      const fileKey = await uploadFile(file, Constants.DATA_MANAGEMENT_MODULES.STANDARDS);
      await httpService.post(ApiEndpoints.BulkUploadDataManagement, {
        fileKey,
        fileName: file.name,
        module: Constants.DATA_MANAGEMENT_MODULES.STANDARDS,
      });
      setCurrentPage(1);
      await refresh();
      toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Standards uploaded successfully', life: 3000 });
    } catch (err: any) {
      let msg = err?.response?.data?.message || err?.message || 'Bulk upload failed';
      toast.current?.show({ severity: 'error', summary: 'Error', detail: msg, life: 3000 });
    } finally {
      setLoading(false);
    }
  };

  const { showImport, ImportPopup } = useExcelImportPopup({
    templateUrl: ImportTemplates.standards,
    onUpload: handleExcelUpload,
    title: 'Import Standards from Excel',
    prominentDownloadLink: true,
  });

  const [providerTypeFilter, setProviderTypeFilter] = useState('');
  const [search, setSearch] = useState('');
  const query = createDataManagementQuery({ providerType: providerTypeFilter, search });
  const {
    items: standards,
    totalRecords,
    page: currentPage,
    setPage: setCurrentPage,
    loading,
    setLoading,
    error,
    refresh,
  } = useDataManagementPagination<any>(ApiEndpoints.GetPagedStandards, query, {
    debounceMs: search ? 300 : 0,
    errorPrefix: 'Failed to fetch standards',
    preserveItemsOnError: true,
    recoverEmptyPageWhenNoResults: true,
  });
  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [selectedStandard, setSelectedStandard] = useState<any>(null);
  const [isAddMode, setIsAddMode] = useState(false);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [standardToDelete, setStandardToDelete] = useState<any>(null);

  const providerTypeOptions = providerTypes && providerTypes.length > 0
    ? providerTypes: Array.from(new Set(standards.map(s => s.providerType).filter(Boolean))).map(pt => ({ label: pt, value: pt }));

  return (
    <div style={{ marginTop: 24, position: 'relative', minHeight: 220 }} aria-busy={loading || lookupsLoading}>
      <DataManagementLoader visible={loading || lookupsLoading} label="Loading standards" />
      <Toast ref={toast} position="top-right" style={{ zIndex: 9999 }} />
      {error && <div style={{ color: 'red' }}>{error}</div>}
      {ImportPopup}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, gap: 16 }}>
        <div style={{ display: 'flex', gap: 12 }}>
          <Button label="Add New Standard" icon="pi pi-plus" onClick={() => {
            setSelectedStandard({ standardId: 0, title: '', description: '', isLocationSpecific: false, performanceAreaTitle: '', performanceAreaId: '', providerType: '' });
            setIsAddMode(true);
            setEditDialogVisible(true);
          }} />
          <Button label="Import" icon="pi pi-upload" className="p-button-secondary" onClick={showImport} style={{ marginLeft: 8 }} />
        </div>
        <div className="standard-search-anim">
          <input
            type="text"
            placeholder="Search by Title..."
            value={search}
            onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
          />
          <span className="pi pi-search" />
        </div>
        <div className="standard-provider-type-dropdown">
          <select
            value={providerTypeFilter}
            onChange={e => { setProviderTypeFilter(e.target.value); setCurrentPage(1); }}
          >
            <option value="">All Provider Types</option>
            {providerTypeOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <span className="pi pi-angle-down" />
        </div>
      </div>
      <div style={{ width: '100%', overflowX: 'auto', position: 'relative' }}>
        <DataManagementDataTable value={standards} dataKey="standardId" page={currentPage} totalRecords={totalRecords}
          onPage={setCurrentPage} loading={loading} emptyMessage="No standards found."
          tableStyle={{ minWidth: 900, background: '#fff' }}>
          <Column header="#" headerStyle={{ width: 64, minWidth: 40 }} body={(_standard: any, options) => (currentPage - 1) * DATA_MANAGEMENT_PAGE_SIZE + options.rowIndex + 1} />
          <Column field="title" header="Title" body={(standard: any) => standard.title || '-'} />
          <Column field="description" header="Description" bodyClassName="data-management-description-cell" body={(standard: any) => standard.description || '-'} />
          <Column field="performanceAreaTitle" header="Performance Area Title" body={(standard: any) => standard.performanceAreaTitle || '-'} />
          <Column field="providerType" header="Provider Type" body={(standard: any) => standard.providerType || '-'} />
          <Column header="Actions" body={(standard: any) => renderStandardActions(
            standard,
            currentStandard => { setSelectedStandard({ ...currentStandard }); setIsAddMode(false); setEditDialogVisible(true); },
            currentStandard => { setStandardToDelete(currentStandard); setDeleteDialogVisible(true); },
          )} />
        </DataManagementDataTable>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog
        header={isAddMode ? "Add Standard" : `Edit Standard${selectedStandard && selectedStandard.title ? ` - ${selectedStandard.title}` : ''}`}
        visible={editDialogVisible}
        style={{ width: '550px', maxWidth: '90vw' }}
        onHide={() => { setEditDialogVisible(false); setIsAddMode(false); }}
        footer={
          <div>
            <Button label="Cancel" onClick={() => { setEditDialogVisible(false); setIsAddMode(false); }} className="p-button-text" />
            <Button label="Save" onClick={async () => {
              const validationErrors = getFormValidationErrors(dataManagementFormConfig.standards, selectedStandard);
              if (validationErrors.length > 0) {
                toast.current?.show({ severity: 'warn', summary: 'Fix the following', detail: validationErrors.join(', '), life: 4000 });
                return;
              }
              const payload = normalizeFieldValues(dataManagementFormConfig.standards, selectedStandard);
              if (isAddMode) {
                try {
                  setLoading(true);
                  await httpService.post(ApiEndpoints.AddStandard, payload);
                  await refresh();
                  setEditDialogVisible(false);
                  setIsAddMode(false);
                  toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Standard added successfully', life: 3000 });
                } catch (err: any) {
                  let msg = err?.response?.data?.message || err?.message || 'Failed to add standard';
                  console.error("Failed to add standard: " + msg);
                  toast.current?.show({ severity: 'error', summary: 'Error', detail: msg, life: 3000 });
                } finally {
                  setLoading(false);
                }
              } else if (selectedStandard) {
                try {
                  setLoading(true);
                  await httpService.put(`${ApiEndpoints.UpdateStandard}/${selectedStandard.standardId}`, { ...payload, isActive: selectedStandard.isActive });
                  await refresh();
                  setEditDialogVisible(false);
                  setIsAddMode(false);
                  toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Standard updated successfully', life: 3000 });
                } catch (err: any) {
                  let msg = err?.response?.data?.message || err?.message || 'Failed to update standard';
                  console.error("Failed to update standard: " + msg);
                  toast.current?.show({ severity: 'error', summary: 'Error', detail: msg, life: 3000 });
                } finally {
                  setLoading(false);
                }
              }
            }} loading={loading} disabled={loading} autoFocus />
          </div>
        }
      >
        <StandardEditForm editStandard={selectedStandard} setEditStandard={setSelectedStandard} performanceAreas={performanceAreas} providerTypesOptions={providerTypeOptions}/>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        header="Confirm Delete"
        visible={deleteDialogVisible}
        style={{ width: '350px' }}
        onHide={() => setDeleteDialogVisible(false)}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button label="Yes" autoFocus={false} onClick={async () => {
              if (!standardToDelete) return;
              try {
                setLoading(true);
                await httpService.delete(`${ApiEndpoints.DeleteStandard}/${standardToDelete.standardId}`);
                await refresh();
                setDeleteDialogVisible(false);
                setStandardToDelete(null);
              } catch (err: any) {
                const msg = err?.response?.data?.message || err?.message || 'Failed to delete standard';
                toast.current?.show({ severity: 'error', summary: 'Error', detail: msg, life: 3000 });
              } finally {
                setLoading(false);
              }
            }} className="p-button-danger" />
            <Button label="No" autoFocus={true} onClick={() => setDeleteDialogVisible(false)} className="p-button-secondary" />
          </div>
        }
      >
        <div style={{ fontSize: 16, margin: '16px 0' }}>
          Are you sure you want to delete this record{standardToDelete && standardToDelete.title ? `: ${standardToDelete.title}` : ''}?
        </div>
      </Dialog>
    </div>
  );
};

interface StandardEditFormProps {
  editStandard: any;
  setEditStandard: (standard: any) => void;
  performanceAreas: any[];
  providerTypesOptions:{
 label: string;
 value: string;
}[];
}

const StandardEditForm: React.FC<StandardEditFormProps> = ({ editStandard, setEditStandard, performanceAreas,providerTypesOptions }) => {
  React.useEffect(() => {
    if (editStandard && editStandard.performanceAreaId && !editStandard.performanceAreaTitle) {
      const pa = performanceAreas.find(pa => pa.performanceAreaId === editStandard.performanceAreaId);
      if (pa) setEditStandard({ ...editStandard, performanceAreaTitle: pa.performanceAreaTitle });
    }
  }, [editStandard, performanceAreas, setEditStandard]);

  const filteredPerformanceAreas = performanceAreas.filter(pa =>
    (!editStandard?.providerType || pa.providerType === editStandard.providerType) ||
    pa.performanceAreaId === editStandard?.performanceAreaId
  );

  return (
    <DataManagementForm
      fields={dataManagementFormConfig.standards}
      values={editStandard}
      onChange={setEditStandard}
      options={{
        providerType: providerTypesOptions,
        performanceAreaTitle: filteredPerformanceAreas.map(area => ({
          label: area.performanceAreaTitle,
          value: area.performanceAreaTitle,
          id: area.performanceAreaId,
        })),
      }}
    />
  );
};

export default StandardsComponent;
